/**
 * Pannello admin del modulo tiro: division, classi di merito, sistemi di punteggio.
 *
 * I tre sono reference data con la stessa forma (`value`, `label`, `discipline`,
 * `order`, `is_active`) più i campi propri di ciascuno, quindi condividono
 * lista, form e salvataggio invece di tre pannelli copiati.
 *
 * Il pezzo meno banale è l'editor dei sistemi di punteggio: il motore
 * (`backend/services/shooting_scoring.py`) non conosce l'IPSC, applica questa
 * tabella. Per questo l'editor deve reggere tre forme diverse senza rami
 * speciali — zone graduate con Major/Minor e cronometro (IPSC), hit/miss puro,
 * solo punti senza tempo — e per questo i preset arrivano precompilati: la
 * configurazione da zero sarebbe una barriera all'adozione interna.
 *
 * La cancellazione è **soft** (`is_active: false`): i risultati storici
 * congelano `division` e `scoring_system_id`, quindi eliminare davvero un
 * documento renderebbe illeggibili punteggi già registrati.
 *
 * Spec: docs/shooting/02-domain-model.md
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Crosshair, Edit, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
	{ key: "divisions", label: "Division" },
	{ key: "classes", label: "Classi di merito" },
	{ key: "scoring-systems", label: "Sistemi di punteggio" },
];

const METRICS = [
	{ value: "hit_factor", label: "Hit factor (punti / tempo)" },
	{ value: "time_plus", label: "Time plus (tempo + penalità)" },
	{ value: "points", label: "Solo punti (senza tempo)" },
];

const ID_FIELD = {
	divisions: "division_id",
	classes: "class_id",
	"scoring-systems": "system_id",
};

const slugify = (raw) =>
	String(raw || "")
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9_]+/g, "_")
		.replace(/^_+|_+$/g, "");

const emptyForm = (kind) => ({
	value: "",
	label: "",
	discipline: "",
	order: 0,
	is_active: true,
	// divisions
	optics: false,
	default_power_factor: "",
	// scoring systems
	metric: "hit_factor",
	min_points: "0",
	zones: [],
	penalties: [],
	_kind: kind,
});

export function ShootingReferenceTab() {
	const [kind, setKind] = useState("divisions");
	const [items, setItems] = useState([]);
	const [sports, setSports] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm("divisions"));
	const [disciplineFilter, setDisciplineFilter] = useState("__all__");

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [data, sportsData] = await Promise.all([
				fetchAPI(`/admin/shooting/${kind}`).catch(() => []),
				fetchAPI("/users/sports/list").catch(() => []),
			]);
			setItems(Array.isArray(data) ? data : []);
			setSports(Array.isArray(sportsData) ? sportsData : []);
		} catch (error) {
			toast.error(`Errore nel caricamento: ${error.message}`);
		} finally {
			setLoading(false);
		}
	}, [kind]);

	useEffect(() => {
		load();
	}, [load]);

	// Le discipline di tiro sono quelle con l'ambito `shooting`: proporre anche
	// CrossFit in un selettore di division sarebbe solo rumore.
	const shootingSports = useMemo(
		() => sports.filter((s) => (s.families || []).includes("shooting")),
		[sports],
	);

	const sportLabel = (value) =>
		sports.find((s) => s.value === value)?.label || value;

	const visibleItems = useMemo(
		() =>
			disciplineFilter === "__all__"
				? items
				: items.filter((i) => i.discipline === disciplineFilter),
		[items, disciplineFilter],
	);

	const switchKind = (nextKind) => {
		setKind(nextKind);
		setEditing(null);
		setForm(emptyForm(nextKind));
	};

	const openNew = () => {
		setEditing(null);
		setForm({
			...emptyForm(kind),
			discipline:
				disciplineFilter !== "__all__"
					? disciplineFilter
					: shootingSports[0]?.value || "",
			order: items.length,
		});
	};

	const openEdit = (item) => {
		setEditing(item);
		setForm({
			...emptyForm(kind),
			value: item.value || "",
			label: item.label || "",
			discipline: item.discipline || "",
			order: item.order ?? 0,
			is_active: item.is_active !== false,
			optics: !!item.optics,
			default_power_factor: item.default_power_factor || "",
			metric: item.metric || "hit_factor",
			min_points:
				item.min_points === null ? "" : String(item.min_points ?? "0"),
			zones: item.zones || [],
			penalties: item.penalties || [],
		});
	};

	// ── Zone e penalità: righe libere, il motore non le interpreta ──
	const updateRow = (listKey, index, patch) =>
		setForm((prev) => ({
			...prev,
			[listKey]: prev[listKey].map((row, i) =>
				i === index ? { ...row, ...patch } : row,
			),
		}));

	const addRow = (listKey) =>
		setForm((prev) => ({
			...prev,
			[listKey]: [
				...prev[listKey],
				listKey === "zones"
					? {
							code: "",
							label: "",
							points_major: "",
							points_minor: "",
							points: "",
							seconds: "",
						}
					: { code: "", label: "", points: "", seconds: "", is_miss: false },
			],
		}));

	const removeRow = (listKey, index) =>
		setForm((prev) => ({
			...prev,
			[listKey]: prev[listKey].filter((_, i) => i !== index),
		}));

	const numberOrUndefined = (raw) => {
		if (raw === "" || raw === null || raw === undefined) return undefined;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : undefined;
	};

	const buildPayload = () => {
		const payload = {
			value: slugify(form.value || form.label),
			label: form.label.trim(),
			discipline: form.discipline,
			order: Number(form.order) || 0,
			is_active: !!form.is_active,
		};
		if (kind === "divisions") {
			payload.optics = !!form.optics;
			payload.default_power_factor = form.default_power_factor || null;
		}
		if (kind === "scoring-systems") {
			payload.metric = form.metric;
			// Campo vuoto = nessun pavimento: i punti possono scendere sotto zero.
			payload.min_points =
				form.min_points === "" ? null : Number(form.min_points) || 0;
			payload.zones = form.zones
				.filter((z) => String(z.code || "").trim())
				.map((z) => ({
					code: String(z.code).trim().toUpperCase(),
					label: z.label || undefined,
					points_major: numberOrUndefined(z.points_major),
					points_minor: numberOrUndefined(z.points_minor),
					points: numberOrUndefined(z.points),
					seconds: numberOrUndefined(z.seconds),
				}));
			payload.penalties = form.penalties
				.filter((p) => String(p.code || "").trim())
				.map((p) => ({
					code: String(p.code).trim().toUpperCase(),
					label: p.label || undefined,
					points: numberOrUndefined(p.points),
					seconds: numberOrUndefined(p.seconds),
					is_miss: !!p.is_miss,
				}));
		}
		return payload;
	};

	const handleSave = async () => {
		if (saving) return;
		if (!form.label.trim()) return toast.error("Inserisci l'etichetta");
		if (!form.discipline) return toast.error("Seleziona la disciplina");
		if (kind === "scoring-systems" && form.zones.length === 0) {
			return toast.error("Serve almeno una zona bersaglio");
		}

		setSaving(true);
		try {
			const payload = buildPayload();
			const itemId = editing?.[ID_FIELD[kind]];
			if (itemId) {
				await fetchAPI(`/admin/shooting/${kind}/${itemId}`, {
					method: "PUT",
					body: JSON.stringify(payload),
				});
				toast.success(`"${payload.label}" aggiornato`);
			} else {
				await fetchAPI(`/admin/shooting/${kind}`, {
					method: "POST",
					body: JSON.stringify(payload),
				});
				toast.success(`"${payload.label}" creato`);
			}
			setEditing(null);
			setForm(emptyForm(kind));
			await load();
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		} finally {
			setSaving(false);
		}
	};

	const handleDeactivate = async (item) => {
		try {
			await fetchAPI(`/admin/shooting/${kind}/${item[ID_FIELD[kind]]}`, {
				method: "DELETE",
			});
			toast.success(`"${item.label}" disattivato`);
			if (editing?.[ID_FIELD[kind]] === item[ID_FIELD[kind]]) setEditing(null);
			await load();
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		}
	};

	const describeItem = (item) => {
		if (kind === "divisions") {
			const parts = [item.optics ? "ottica" : "senza ottica"];
			if (item.default_power_factor) parts.push(item.default_power_factor);
			return parts.join(" · ");
		}
		if (kind === "scoring-systems") {
			const metric =
				METRICS.find((m) => m.value === item.metric)?.label || item.metric;
			return `${metric} · ${(item.zones || []).length} zone · ${(item.penalties || []).length} penalità`;
		}
		return null;
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				{KINDS.map((entry) => (
					<Button
						key={entry.key}
						size="sm"
						variant={kind === entry.key ? "default" : "secondary"}
						className={`rounded-sm ${kind === entry.key ? "bg-volt hover:bg-volt/90" : ""}`}
						onClick={() => switchKind(entry.key)}
					>
						{entry.label}
					</Button>
				))}
				<div className="ml-auto">
					<Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm w-56">
							<SelectValue />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="__all__">Tutte le discipline</SelectItem>
							{shootingSports.map((s) => (
								<SelectItem key={s.value} value={s.value}>
									{s.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* ── Lista ── */}
				<Card className="bg-card border-border rounded-sm">
					<CardHeader>
						<CardTitle className="font-heading text-xl flex items-center gap-2">
							<Crosshair className="w-5 h-5 text-volt" />
							{KINDS.find((k) => k.key === kind)?.label}
						</CardTitle>
						<p className="text-sm text-muted-foreground">
							Il motore di punteggio applica questa configurazione e non conosce
							il regolamento: cambiarla qui cambia il calcolo.
						</p>
					</CardHeader>
					<CardContent>
						{loading ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
							</div>
						) : visibleItems.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								Nessun elemento. Usa "Nuovo" per crearne uno.
							</p>
						) : (
							<div className="space-y-2">
								{visibleItems.map((item) => (
									<div
										key={item[ID_FIELD[kind]]}
										className="flex items-center justify-between p-3 bg-secondary rounded-sm gap-3"
									>
										<div className="min-w-0">
											<p className="font-medium truncate">{item.label}</p>
											<p className="text-xs text-muted-foreground font-mono truncate">
												{item.value} · {sportLabel(item.discipline)}
											</p>
											{describeItem(item) && (
												<p className="text-xs text-muted-foreground mt-0.5">
													{describeItem(item)}
												</p>
											)}
										</div>
										<div className="flex items-center gap-1.5 shrink-0">
											<Badge
												className={
													item.is_active !== false
														? "bg-green-500/20 text-green-400 border-0"
														: "bg-red-500/20 text-red-400 border-0"
												}
											>
												{item.is_active !== false ? "Attivo" : "Disattivato"}
											</Badge>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-sm"
												onClick={() => openEdit(item)}
											>
												<Edit className="w-4 h-4" />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-sm hover:text-red-500"
												onClick={() => handleDeactivate(item)}
											>
												<Trash2 className="w-4 h-4" />
											</Button>
										</div>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				{/* ── Form ── */}
				<Card className="bg-card border-border rounded-sm">
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle className="font-heading text-xl">
								{editing ? "Modifica" : "Nuovo"}
							</CardTitle>
							<Button
								variant="outline"
								size="sm"
								className="rounded-sm"
								onClick={openNew}
								disabled={saving}
							>
								<Plus className="w-4 h-4 mr-1.5" />
								{editing ? "Annulla modifica" : "Svuota campi"}
							</Button>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<Label>Disciplina</Label>
							<Select
								value={form.discipline || "__none__"}
								onValueChange={(v) =>
									setForm({ ...form, discipline: v === "__none__" ? "" : v })
								}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm">
									<SelectValue placeholder="Seleziona disciplina" />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									{shootingSports.length === 0 && (
										<SelectItem value="__none__">
											Nessuna disciplina di tiro configurata
										</SelectItem>
									)}
									{shootingSports.map((s) => (
										<SelectItem key={s.value} value={s.value}>
											{s.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<Label>Etichetta</Label>
							<Input
								value={form.label}
								onChange={(e) =>
									setForm({
										...form,
										label: e.target.value,
										...(!editing && { value: slugify(e.target.value) }),
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="Es: Production Optics"
							/>
						</div>

						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-2">
								<Label>Value (ID)</Label>
								<Input
									value={form.value}
									onChange={(e) =>
										setForm({ ...form, value: slugify(e.target.value) })
									}
									className="bg-secondary border-transparent rounded-sm font-mono text-sm"
								/>
							</div>
							<div className="space-y-2">
								<Label>Ordine</Label>
								<Input
									type="number"
									value={form.order}
									onChange={(e) => setForm({ ...form, order: e.target.value })}
									className="bg-secondary border-transparent rounded-sm"
								/>
							</div>
						</div>

						{kind === "divisions" && (
							<div className="grid grid-cols-2 gap-3">
								<div className="space-y-2">
									<Label>Ottica</Label>
									<Select
										value={form.optics ? "yes" : "no"}
										onValueChange={(v) =>
											setForm({ ...form, optics: v === "yes" })
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="yes">Con ottica</SelectItem>
											<SelectItem value="no">Senza ottica</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>Power factor di default</Label>
									<Select
										value={form.default_power_factor || "__none__"}
										onValueChange={(v) =>
											setForm({
												...form,
												default_power_factor: v === "__none__" ? "" : v,
											})
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="__none__">Nessuno</SelectItem>
											<SelectItem value="major">Major</SelectItem>
											<SelectItem value="minor">Minor</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
						)}

						{kind === "scoring-systems" && (
							<>
								<div className="grid grid-cols-2 gap-3">
									<div className="space-y-2">
										<Label>Metrica di ranking</Label>
										<Select
											value={form.metric}
											onValueChange={(v) => setForm({ ...form, metric: v })}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm">
												<SelectValue />
											</SelectTrigger>
											<SelectContent className="bg-card border-border">
												{METRICS.map((m) => (
													<SelectItem key={m.value} value={m.value}>
														{m.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div className="space-y-2">
										<Label>Punti minimi</Label>
										<Input
											value={form.min_points}
											onChange={(e) =>
												setForm({ ...form, min_points: e.target.value })
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="0 = non scende sotto zero"
										/>
									</div>
								</div>

								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label>Zone bersaglio</Label>
										<Button
											type="button"
											size="sm"
											variant="outline"
											className="rounded-sm"
											onClick={() => addRow("zones")}
										>
											<Plus className="w-3.5 h-3.5 mr-1" /> Zona
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										La prima zona è quella "migliore": definisce la percentuale
										di accuratezza. Compila Major/Minor per l'IPSC, oppure il
										solo campo Punti per un sistema senza power factor.
									</p>
									{/* Intestazioni di colonna. I placeholder spariscono alla
									    prima lettera: con quattro caselle in fila, appena si
									    scrive non si sa più quale sia quale. */}
									{form.zones.length > 0 && (
										<div className="grid grid-cols-[3rem,1fr,1fr,1fr,2rem] gap-1.5 px-1">
											{["Zona", "Major", "Minor", "Punti", ""].map((head) => (
												<span
													key={head}
													className="text-[10px] uppercase tracking-wider text-muted-foreground"
												>
													{head}
												</span>
											))}
										</div>
									)}
									{/* Riga di esempio: l'IPSC, che è il caso che si copia. */}
									{form.zones.length === 0 && (
										<p className="text-[11px] text-muted-foreground font-mono">
											IPSC: A 5/5 · C 4/3 · D 2/1
										</p>
									)}
									{form.zones.map((zone, index) => (
										<div
											key={`zone_${index}`}
											className="grid grid-cols-[3rem,1fr,1fr,1fr,2rem] gap-1.5 items-center"
										>
											<Input
												value={zone.code || ""}
												onChange={(e) =>
													updateRow("zones", index, { code: e.target.value })
												}
												className="bg-secondary border-transparent rounded-sm font-mono text-xs px-2"
												placeholder="A"
											/>
											<Input
												value={zone.points_major ?? ""}
												onChange={(e) =>
													updateRow("zones", index, {
														points_major: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder="Major"
											/>
											<Input
												value={zone.points_minor ?? ""}
												onChange={(e) =>
													updateRow("zones", index, {
														points_minor: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder="Minor"
											/>
											<Input
												value={zone.points ?? ""}
												onChange={(e) =>
													updateRow("zones", index, { points: e.target.value })
												}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder="Punti"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="rounded-sm hover:text-red-500 h-8 w-8"
												onClick={() => removeRow("zones", index)}
											>
												<Trash2 className="w-3.5 h-3.5" />
											</Button>
										</div>
									))}
								</div>

								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label>Penalità</Label>
										<Button
											type="button"
											size="sm"
											variant="outline"
											className="rounded-sm"
											onClick={() => addRow("penalties")}
										>
											<Plus className="w-3.5 h-3.5 mr-1" /> Penalità
										</Button>
									</div>
									<p className="text-xs text-muted-foreground">
										Punti negativi per sottrarre (IPSC: −10), secondi per il
										time-plus. "Miss" marca i colpi non andati a bersaglio:
										entrano nella percentuale di colpi a segno, una procedurale
										no.
									</p>
									{form.penalties.length > 0 && (
										<div className="grid grid-cols-[3.5rem,1fr,1fr,auto,2rem] gap-1.5 px-1">
											{["Codice", "Punti", "Secondi", "Miss", ""].map((head) => (
												<span
													key={head}
													className="text-[10px] uppercase tracking-wider text-muted-foreground"
												>
													{head}
												</span>
											))}
										</div>
									)}
									{form.penalties.length === 0 && (
										<p className="text-[11px] text-muted-foreground font-mono">
											IPSC: M −10 · NS −10 · PROC −10
										</p>
									)}
									{form.penalties.map((penalty, index) => (
										<div
											key={`pen_${index}`}
											className="grid grid-cols-[3.5rem,1fr,1fr,auto,2rem] gap-1.5 items-center"
										>
											<Input
												value={penalty.code || ""}
												onChange={(e) =>
													updateRow("penalties", index, {
														code: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm font-mono text-xs px-2"
												placeholder="M"
											/>
											<Input
												value={penalty.points ?? ""}
												onChange={(e) =>
													updateRow("penalties", index, {
														points: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder="Punti"
											/>
											<Input
												value={penalty.seconds ?? ""}
												onChange={(e) =>
													updateRow("penalties", index, {
														seconds: e.target.value,
													})
												}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder="Secondi"
											/>
											<Button
												type="button"
												size="sm"
												variant={penalty.is_miss ? "default" : "secondary"}
												className={`rounded-sm text-xs px-2 ${penalty.is_miss ? "bg-volt hover:bg-volt/90" : ""}`}
												onClick={() =>
													updateRow("penalties", index, {
														is_miss: !penalty.is_miss,
													})
												}
											>
												Miss
											</Button>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="rounded-sm hover:text-red-500 h-8 w-8"
												onClick={() => removeRow("penalties", index)}
											>
												<Trash2 className="w-3.5 h-3.5" />
											</Button>
										</div>
									))}
								</div>
							</>
						)}

						<Button
							onClick={handleSave}
							disabled={saving || !form.label.trim()}
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							{saving ? (
								<>
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
									Salvataggio...
								</>
							) : editing ? (
								"Salva modifiche"
							) : (
								"Crea"
							)}
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
